import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  useTheme,
  Fab,
  Drawer,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Campaign as CampaignIcon,
  Event as EventIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  PlayCircle as PlayCircleIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import PageContainer from "../PageContainer";
import OrganisationSetupBanner from "../shared/OrganisationSetupBanner";
import { NoticeboardButton } from "../shared";
import GettingStartedChecklist from "../shared/GettingStartedChecklist";
import DashboardModals, {
  type DashboardModalsProps,
} from "./DashboardModals";
import DraftNoticesToast from "./DraftNoticesToast";
import JoinRequestsToast from "./JoinRequestsToast";
import DidYouKnowCard from "./DidYouKnowCard";
import StatsPanel from "./StatsPanel";
import { useMobileFabVisibility } from "../../hooks/useMobileFabVisibility";
import { mobileFabWithScrollVisibility } from "../../styles/managementShared";
import { DASHBOARD_TUTORIAL_VIDEOS } from "../../constants/dashboardTutorialVideos";
import type { getDashboardDisplayState } from "../../domain/dashboard/dashboardConditions";

type DashboardDisplayState = ReturnType<typeof getDashboardDisplayState>;

type MobileHomeQuickAction = {
  label: string;
  description: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  onClick: () => void;
};

type MobileDrawerQuickAction = {
  section: "create";
  label: string;
  description: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  onClick: () => void;
};

type ChecklistItem = {
  label: string;
  done: boolean;
  onClick: () => void;
  videoPath: string;
  videoTitle: string;
};

export type DashboardMobileHomeProps = {
  mobileGreetingName: string;
  onCompleteSetup: () => void;
  handleCreateNotice: () => void;
  handleOpenAddEventModal: () => void;
  handlePlayVideo: (videoPath: string, title: string) => void;
  handleViewNotices: () => void;
  handleAdCenter: () => void;
  mobileHomeQuickActions: MobileHomeQuickAction[];
  mobileDrawerQuickActions: MobileDrawerQuickAction[];
  draftNoticesCount: number;
  joinRequestsCount: number;
  handleViewJoinRequests: () => void;
  display: DashboardDisplayState;
  isAdCenterEnabled: boolean;
  checklistItems: ChecklistItem[];
  stats: {
    totalUsers: number;
    totalNotices: number;
    upcomingEvents: number;
    activeAdverts: number;
  };
  dashboardModalsProps: DashboardModalsProps;
};

const DashboardMobileHome: React.FC<DashboardMobileHomeProps> = ({
  mobileGreetingName,
  onCompleteSetup,
  handleCreateNotice,
  handleOpenAddEventModal,
  handlePlayVideo,
  handleViewNotices,
  handleAdCenter,
  mobileHomeQuickActions,
  mobileDrawerQuickActions,
  draftNoticesCount,
  joinRequestsCount,
  handleViewJoinRequests,
  display,
  isAdCenterEnabled,
  checklistItems,
  stats,
  dashboardModalsProps,
}) => {
  const theme = useTheme();
  const mobileFabVisible = useMobileFabVisibility(true, 450, {
    hideWhileScrolling: false,
  });
  const [isMobileQuickMenuOpen, setIsMobileQuickMenuOpen] = useState(false);

  return (
    <PageContainer sx={{ mt: -1, pt: 2.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <OrganisationSetupBanner
          onCompleteSetup={onCompleteSetup}
          sx={{ mb: 0.5 }}
        />

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 2.25,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.14),
            background: `radial-gradient(circle at 85% 15%, ${alpha(
              theme.palette.primary.main,
              0.12,
            )} 0%, transparent 45%), linear-gradient(135deg, ${theme.palette.gradients.heroSurface[0]} 0%, ${theme.palette.gradients.heroSurface[1]} 100%)`,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr) 126px",
                sm: "minmax(0, 1fr) 118px",
              },
              alignItems: "start",
              gap: 1,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "2.2rem", sm: "2.2rem" },
                  lineHeight: 1.02,
                  letterSpacing: "-0.01em",
                  fontWeight: 400,
                  color: "text.primary",
                }}
              >
                Hello,{" "}
                <Box
                  component="span"
                  sx={{ color: "primary.main", fontWeight: 400 }}
                >
                  {mobileGreetingName}
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 1.1,
                  color: "text.secondary",
                  fontSize: "0.99rem",
                  lineHeight: 1.35,
                  maxWidth: 260,
                }}
              >
                Manage notices, events, and communication all in one place.
              </Typography>
              <Box
                sx={{
                  mt: 2.2,
                  display: "flex",
                  gap: 0.85,
                  flexWrap: "nowrap",
                  alignItems: "center",
                }}
              >
                <NoticeboardButton
                  variant="primary"
                  onClick={handleCreateNotice}
                  sx={{
                    borderRadius: 999,
                    px: 1.55,
                    py: 0.9,
                    fontWeight: 600,
                    fontSize: "0.86rem",
                    whiteSpace: "nowrap",
                    minHeight: 42,
                    minWidth: 150,
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.7,
                    }}
                  >
                    <CampaignIcon sx={{ fontSize: 19 }} />
                    <span>Creators Hub</span>
                  </Box>
                </NoticeboardButton>
                <NoticeboardButton
                  variant="secondary"
                  onClick={handleOpenAddEventModal}
                  sx={{
                    borderRadius: 999,
                    px: 1.55,
                    py: 0.9,
                    fontWeight: 600,
                    fontSize: "0.86rem",
                    whiteSpace: "nowrap",
                    minHeight: 42,
                    minWidth: 150,
                    bgcolor: "common.white",
                    color: "primary.main",
                    border: "1px solid",
                    borderColor: "primary.main",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.65,
                    }}
                  >
                    <EventIcon sx={{ fontSize: 18 }} />
                    <span>Create an event</span>
                  </Box>
                </NoticeboardButton>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", pt: 0.25 }}>
              <Box
                component="img"
                src="/create/worksheets.webp"
                alt="Dashboard hero"
                sx={{
                  width: { xs: 126, sm: 118 },
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "1.6rem",
              lineHeight: 1.1,
              fontWeight: 400,
              color: "text.primary",
            }}
          >
            Quick Actions
          </Typography>
          <Tooltip title="Watch create notice tutorial">
            <IconButton
              onClick={() =>
                handlePlayVideo(
                  DASHBOARD_TUTORIAL_VIDEOS.createNotice.path,
                  DASHBOARD_TUTORIAL_VIDEOS.createNotice.title,
                )
              }
              aria-label="Watch create notice tutorial"
              sx={{ color: "primary.main" }}
            >
              <PlayCircleIcon sx={{ fontSize: 30 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
          {mobileHomeQuickActions.map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              component="div"
              role="button"
              tabIndex={0}
              aria-label={`${item.label}: ${item.description}`}
              onClick={item.onClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  item.onClick();
                }
              }}
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  transform: "translateY(-1px)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: item.iconBg || alpha(theme.palette.primary.main, 0.1),
                  color: item.iconColor || "primary.main",
                  mb: 1,
                }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "0.98rem",
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.84rem",
                  color: "text.secondary",
                  minHeight: 36,
                }}
              >
                {item.description}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.25 }}>
                <ArrowForwardIosIcon
                  sx={{
                    fontSize: (th) => th.typography.body2.fontSize,
                    color: "primary.main",
                  }}
                />
              </Box>
            </Paper>
          ))}
        </Box>

        <DraftNoticesToast
          count={draftNoticesCount}
          onViewDrafts={handleViewNotices}
        />

        <JoinRequestsToast
          count={joinRequestsCount}
          onViewRequests={handleViewJoinRequests}
        />

        {display.showStats && (
          <Box sx={{ mt: 0.5 }}>
            <StatsPanel
              totalUsers={stats.totalUsers}
              totalNotices={stats.totalNotices}
              upcomingEvents={stats.upcomingEvents}
              activeAdverts={stats.activeAdverts}
              showActiveAdverts={isAdCenterEnabled}
            />
          </Box>
        )}

        {display.showNoNoticesCard && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <GettingStartedChecklist
              items={checklistItems}
              onPlayVideo={handlePlayVideo}
              vertical
            />
            {display.showAdCenterSetupCard && isAdCenterEnabled && (
              <DidYouKnowCard onSetupAdCenter={handleAdCenter} />
            )}
          </Box>
        )}
      </Box>

      <DashboardModals {...dashboardModalsProps} />

      <Fab
        aria-label="Open quick actions"
        onClick={() => setIsMobileQuickMenuOpen(true)}
        sx={mobileFabWithScrollVisibility(
          {
            position: "fixed",
            right: 20,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 64px)",
            width: 58,
            height: 58,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": {
              bgcolor: "primary.dark",
            },
            boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
            zIndex: theme.zIndex.speedDial,
          },
          mobileFabVisible,
        )}
      >
        <AddIcon sx={{ fontSize: 30 }} />
      </Fab>

      <Drawer
        anchor="bottom"
        open={isMobileQuickMenuOpen}
        onClose={() => setIsMobileQuickMenuOpen(false)}
        transitionDuration={{ enter: 180, exit: 100 }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 40px)",
            px: 2,
            pt: 1,
            maxHeight: "78vh",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
          <Box
            sx={{
              width: 44,
              height: 5,
              borderRadius: 999,
              bgcolor: "grey.300",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {mobileDrawerQuickActions
            .filter((action) => action.section === "create")
            .map((action) => (
              <Paper
                key={`mobile-create-${action.label}`}
                elevation={0}
                role="button"
                tabIndex={0}
                onClick={() => {
                  setIsMobileQuickMenuOpen(false);
                  action.onClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsMobileQuickMenuOpen(false);
                    action.onClick();
                  }
                }}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  minHeight: 124,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "50%",
                    bgcolor: action.iconBg,
                    color: action.iconColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    "& svg": {
                      fontSize: 30,
                    },
                  }}
                >
                  {action.icon}
                </Box>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.8rem",
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  {action.label}
                </Typography>
              </Paper>
            ))}
        </Box>
      </Drawer>
    </PageContainer>
  );
};

export default DashboardMobileHome;
